<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getLanguageName } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import ContactDetailsItem from './ContactDetailsItem.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';

const props = defineProps({
  conversationAttributes: {
    type: Object,
    default: () => ({}),
  },
  contactAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const referer = computed(() => props.conversationAttributes.referer);
const initiatedAt = computed(
  () => props.conversationAttributes.initiated_at?.timestamp
);

const browserInfo = computed(() => props.conversationAttributes.browser);

const browserName = computed(() => {
  if (!browserInfo.value) return '';
  const { browser_name: name = '', browser_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const browserLanguage = computed(() =>
  getLanguageName(props.conversationAttributes.browser_language)
);

const platformName = computed(() => {
  if (!browserInfo.value) return '';
  const { platform_name: name = '', platform_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const createdAtIp = computed(() => props.contactAttributes.created_at_ip);

const whatsappAd = computed(
  () => props.conversationAttributes.whatsapp_ad || {}
);

const whatsappAdTitle = computed(
  () =>
    whatsappAd.value.headline ||
    whatsappAd.value.body ||
    whatsappAd.value.source_url ||
    whatsappAd.value.source_id
);

const whatsappAdImageUrl = computed(
  () => whatsappAd.value.image_url || whatsappAd.value.thumbnail_url
);

const whatsappAdFields = computed(() =>
  [
    {
      key: 'headline',
      label: t('CONTACT_PANEL.WHATSAPP_AD_FIELDS.HEADLINE'),
      value: whatsappAd.value.headline,
    },
    {
      key: 'body',
      label: t('CONTACT_PANEL.WHATSAPP_AD_FIELDS.BODY'),
      value: whatsappAd.value.body,
    },
    {
      key: 'source_type',
      label: t('CONTACT_PANEL.WHATSAPP_AD_FIELDS.SOURCE_TYPE'),
      value: whatsappAd.value.source_type,
    },
    {
      key: 'source_id',
      label: t('CONTACT_PANEL.WHATSAPP_AD_FIELDS.SOURCE_ID'),
      value: whatsappAd.value.source_id,
    },
    {
      key: 'media_type',
      label: t('CONTACT_PANEL.WHATSAPP_AD_FIELDS.MEDIA_TYPE'),
      value: whatsappAd.value.media_type,
    },
    {
      key: 'ctwa_clid',
      label: t('CONTACT_PANEL.WHATSAPP_AD_FIELDS.CTWA_CLID'),
      value: whatsappAd.value.ctwa_clid,
    },
    {
      key: 'source_url',
      label: t('CONTACT_PANEL.WHATSAPP_AD_FIELDS.SOURCE_URL'),
      value: whatsappAd.value.source_url,
      isLink: true,
    },
    {
      key: 'video_url',
      label: t('CONTACT_PANEL.WHATSAPP_AD_FIELDS.VIDEO_URL'),
      value: whatsappAd.value.video_url,
      isLink: true,
    },
  ].filter(field => !!field.value)
);

const staticElements = computed(() =>
  [
    {
      content: initiatedAt,
      title: t('CONTACT_PANEL.INITIATED_AT'),
      key: 'static-initiated-at',
      type: 'static_attribute',
    },
    {
      content: browserLanguage,
      title: t('CONTACT_PANEL.BROWSER_LANGUAGE'),
      key: 'static-browser-language',
      type: 'static_attribute',
    },
    {
      content: referer,
      title: t('CONTACT_PANEL.INITIATED_FROM'),
      key: 'static-referer',
      type: 'static_attribute',
    },
    {
      content: browserName,
      title: t('CONTACT_PANEL.BROWSER'),
      key: 'static-browser',
      type: 'static_attribute',
    },
    {
      content: platformName,
      title: t('CONTACT_PANEL.OS'),
      key: 'static-platform',
      type: 'static_attribute',
    },
    {
      content: createdAtIp,
      title: t('CONTACT_PANEL.IP_ADDRESS'),
      key: 'static-ip-address',
      type: 'static_attribute',
    },
  ].filter(attribute => !!attribute.content.value)
);
</script>

<template>
  <div class="conversation--details">
    <ContactDetailsItem
      v-if="whatsappAdTitle"
      :title="t('CONTACT_PANEL.WHATSAPP_AD')"
      :value="whatsappAdTitle"
      class="border-b border-n-weak/50 dark:border-n-weak/90"
    >
      <div class="space-y-3">
        <img
          v-if="whatsappAdImageUrl"
          :src="whatsappAdImageUrl"
          :alt="t('CONTACT_PANEL.WHATSAPP_AD_IMAGE_ALT')"
          class="object-cover w-full border rounded-lg max-h-40 border-n-weak"
        />
        <div class="space-y-2">
          <div
            v-for="field in whatsappAdFields"
            :key="field.key"
            class="text-sm"
          >
            <div
              class="mb-0.5 text-xs font-medium uppercase tracking-wide text-n-slate-11"
            >
              {{ field.label }}
            </div>
            <a
              v-if="field.isLink"
              :href="field.value"
              rel="noopener noreferrer nofollow"
              target="_blank"
              class="break-words text-n-brand"
            >
              {{ field.value }}
            </a>
            <p v-else class="text-n-slate-12 break-words">
              {{ field.value }}
            </p>
          </div>
        </div>
      </div>
    </ContactDetailsItem>
    <CustomAttributes
      :static-elements="staticElements"
      attribute-class="conversation--attribute"
      attribute-from="conversation_panel"
      attribute-type="conversation_attribute"
    >
      <template #staticItem="{ element }">
        <ContactDetailsItem
          :key="element.title"
          :title="element.title"
          :value="element.content.value"
        >
          <a
            v-if="element.key === 'static-referer'"
            :href="element.content.value"
            rel="noopener noreferrer nofollow"
            target="_blank"
            class="text-n-brand"
          >
            {{ element.content.value }}
          </a>
          <template v-else>
            {{ element.content.value }}
          </template>
        </ContactDetailsItem>
      </template>
    </CustomAttributes>
  </div>
</template>
